import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

const BusinessCombobox = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    // If value matches an option, show its label, otherwise show the value itself (keep text only)
    const option = options.find((opt) => opt.value === value);
    setInputValue(option ? option.label : (value || ""));
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setIsOpen(true);
    // See if they exactly typed a label, if so map to value, else use the raw text
    const option = options.find((opt) => opt.label.toLowerCase() === val.toLowerCase());
    onChange(option ? option.value : val);
  };

  const handleOptionClick = (opt) => {
    setInputValue(opt.label);
    onChange(opt.value);
    setIsOpen(false);
  };

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(inputValue.toLowerCase()) || 
    opt.value.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div ref={wrapperRef} className="relative mt-1">
      <div className="relative w-full">
        <input
          type="text"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm text-black placeholder:text-gray-400"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex items-center pr-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </button>
      </div>

      {isOpen && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {filteredOptions.length === 0 ? (
            <li 
              className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-indigo-600 hover:bg-indigo-50"
              onClick={() => setIsOpen(false)}
            >
              Keep "{inputValue}"
            </li>
          ) : (
            filteredOptions.map((opt) => (
              <li
                key={opt.value}
                className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white"
                onClick={() => handleOptionClick(opt)}
              >
                <span className="block truncate">{opt.label}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default BusinessCombobox;
